import React, { useRef } from 'react';
import { MdCloudUpload, MdClose } from 'react-icons/md';
import type { UploadedFile } from '../../types';

interface FileUploadZoneProps {
    files: UploadedFile[];
    setFiles: (fn: (prev: UploadedFile[]) => UploadedFile[]) => void;
}

export default function FileUploadZone({ files, setFiles }: FileUploadZoneProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files;
        if (!selected) return;
        setFiles((prev) => [
            ...prev,
            ...Array.from(selected).map((f) => ({ file: f, name: f.name, progress: 100 })),
        ]);
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setFiles((prev) => [
            ...prev,
            ...Array.from(e.dataTransfer.files).map((f) => ({ file: f, name: f.name, progress: 100 })),
        ]);
    };

    return (
        <>
            <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#eee] bg-[#fafafa] px-6 py-12"
            >
                <MdCloudUpload size={40} className="text-primary" />
                <p className="mt-3 text-center text-[13px] font-semibold text-secondary">
                    Drag & drop files or{' '}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="font-semibold text-primary"
                    >
                        Browse
                    </button>
                </p>
                <p className="mt-1 text-[11px] text-[#999]">Supported formats: PDF, DOCX, DOC, Word, TXT</p>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.txt,image/*,video/*"
                    className="hidden"
                    aria-label="Upload files"
                    onChange={handleFileSelect}
                />
            </div>

            {files.length > 0 && (
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    {files.map((file, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] text-secondary">{file.name}</p>
                                <div className="mt-1 h-1 w-full rounded-full bg-[#eee]">
                                    <div
                                        className="h-full rounded-full bg-green-500"
                                        style={{ width: `${file.progress}%` }}
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                title="Remove file"
                                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                                className="shrink-0 text-[#999] hover:text-red-500"
                            >
                                <MdClose size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
